import React, { useState } from "react";
import { Card, Typography, Space, Row, Col } from "antd";

const { Title, Paragraph, Text } = Typography;

const ProblemGoalIntelligence = () => {
  const [currentScenario, setCurrentScenario] = useState(0);

  const scenarioOptions = [
    { key: 0, label: "Low Battery" },
    { key: 1, label: "Hungry" },
    { key: 2, label: "Forgot Birthday" },
  ];

  const scenarios = [
    {
      title: "Low Battery",
      problem: "Your phone battery is at 5%",
      goal: "Charge your phone before it dies",
      intelligentActions: [
        "Look for nearest charging station",
        "Use power-saving mode immediately",
        "Ask a friend for a charger",
        "Find a cafe with outlets",
      ],
    },
    {
      title: "Hungry",
      problem: "You're hungry but don't know what to eat",
      goal: "Find satisfying food quickly",
      intelligentActions: [
        "Check what's in your fridge",
        "Think about your cravings",
        "Consider cooking time available",
        "Check food delivery apps for deals",
      ],
    },
    {
      title: "Forgot Birthday",
      problem: "You forgot your friend's birthday",
      goal: "Make it up to your friend",
      intelligentActions: [
        "Send a sincere apology message",
        "Plan a surprise celebration",
        "Make a thoughtful handmade gift",
        "Set reminder for next year",
      ],
    },
  ];

  const consolidatedData = [
    {
      concept: "Problem",
      description: "Something that needs to be solved or improved. This is where things begin.",
      example: "Lost in a new mall",
      aiExample: "Current board position (Chess AI)",
    },
    {
      concept: "Goal",
      description: "What you want to achieve - the solution to your problem.",
      example: "Find the food court",
      aiExample: "Checkmate opponent (Chess AI)",
    },
    {
      concept: "Intelligence",
      description: "Using what you know to solve problems in smart, effective ways.",
      example: "Look for signs or ask for help",
      aiExample: "Evaluate millions of moves (Chess AI)",
    },
  ];

  const currentExample = scenarios[currentScenario];

  return (
    <div className="page-container">
      <header style={{ textAlign: "center", marginBottom: "32px" }} className="fade-in-down">
        <Title level={1}>Problem → Goal → Intelligence</Title>
        <Paragraph style={{ fontSize: "18px" }}>
          Learn how AI (and you!) solve problems using smart thinking
        </Paragraph>
      </header>

      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Consolidated Concepts and AI Examples - Responsive Table/List */}
        <Card title={<span style={{ color: "#0071e3" }}>Concepts & Examples</span>}>
          {/* Desktop Table View */}
          <div className="desktop-table">
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ padding: "12px 16px", textAlign: "left", borderBottom: "2px solid #0071e3", width: "15%" }}>
                      <Text strong>Concept</Text>
                    </th>
                    <th style={{ padding: "12px 16px", textAlign: "left", borderBottom: "2px solid #0071e3", width: "35%" }}>
                      <Text strong>Description</Text>
                    </th>
                    <th style={{ padding: "12px 16px", textAlign: "left", borderBottom: "2px solid #0071e3", width: "25%" }}>
                      <Text strong>Simple Example</Text>
                    </th>
                    <th style={{ padding: "12px 16px", textAlign: "left", borderBottom: "2px solid #0071e3", width: "25%" }}>
                      <Text strong>AI Example</Text>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {consolidatedData.map((row, idx) => (
                    <tr key={idx} style={{ borderBottom: idx < consolidatedData.length - 1 ? "2px solid #f0f0f0" : "none" }}>
                      <td style={{ padding: "16px", verticalAlign: "top", textAlign: "left" }}>
                        <Text strong style={{ color: "#0071e3" }}>{row.concept}</Text>
                      </td>
                      <td style={{ padding: "16px", textAlign: "left", verticalAlign: "top" }}>
                        {row.description}
                      </td>
                      <td style={{ padding: "16px", textAlign: "left", verticalAlign: "top" }}>
                        {row.example}
                      </td>
                      <td style={{ padding: "16px", textAlign: "left", verticalAlign: "top" }}>
                        {row.aiExample}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="mobile-cards">
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              {consolidatedData.map((item, idx) => (
                <Card key={idx} size="small" style={{ border: "1px solid #0071e3", borderRadius: "8px" }}>
                  <Text strong style={{ color: "#0071e3", fontSize: "16px" }}>{item.concept}</Text>
                  <div style={{ marginTop: "8px" }}>
                    <Text strong>Description: </Text>
                    <Text>{item.description}</Text>
                  </div>
                  <div style={{ marginTop: "8px" }}>
                    <Text strong>Simple Example: </Text>
                    <Text>{item.example}</Text>
                  </div>
                  <div style={{ marginTop: "8px" }}>
                    <Text strong>AI Example: </Text>
                    <Text>{item.aiExample}</Text>
                  </div>
                </Card>
              ))}
            </Space>
          </div>
        </Card>

        {/* Interactive Demo - UNCHANGED */}
        <Card title={<span style={{ color: "#0071e3" }}>Interactive Demo</span>}>
          <Paragraph>
            Choose a real-life scenario and see how Problem → Goal → Intelligence work together
          </Paragraph>

          <Row gutter={[16, 16]} justify="center">
            {scenarioOptions.map((s) => (
              <Col xs={24} sm={12} md={8} key={s.key}>
                <Card
                  hoverable={false}
                  onClick={() => setCurrentScenario(s.key)}
                  style={{
                    cursor: "pointer",
                    border: currentScenario === s.key ? "2px solid #667eea" : "2px solid #f0f0f0",
                    textAlign: "center",
                    fontWeight: 500,
                  }}
                >
                  {s.label}
                </Card>
              </Col>
            ))}
          </Row>

          <Space direction="vertical" size="middle" style={{ width: "100%", marginTop: "24px" }}>
            <Card size="small" style={{ backgroundColor: "#fff1f0", borderColor: "#ff4d4f" }}>
              <Text strong>Problem: </Text>
              <Text>{currentExample.problem}</Text>
            </Card>

            <Card size="small" style={{ backgroundColor: "#e6f7ff", borderColor: "#1890ff" }}>
              <Text strong>Goal: </Text>
              <Text>{currentExample.goal}</Text>
            </Card>

            <Card size="small" style={{ borderColor: "#52c41a", backgroundColor: "#f6ffed" }}>
              <Text strong>Intelligent Solutions:</Text>
              <ul style={{ paddingLeft: "20px", marginTop: "8px", marginBottom: 0 }}>
                {currentExample.intelligentActions.map((action, index) => (
                  <li key={index}>{action}</li>
                ))}
              </ul>
            </Card>
          </Space>
        </Card>

        {/* More AI Examples */}
        <Card title={<span style={{ color: "#0071e3" }}>More AI Examples</span>}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <div style={{ padding: "16px", borderRadius: "8px", height: "100%", border: "1px solid #0071e3" }}>
                <Text strong>Self-Driving Car</Text>
                <ul style={{ paddingLeft: "20px", marginTop: "8px", marginBottom: 0, fontSize: "14px" }}>
                  <li>
                    <Text strong>Problem:</Text> Navigate from A to B
                  </li>
                  <li>
                    <Text strong>Goal:</Text> Arrive safely
                  </li>
                  <li>
                    <Text strong>Intelligence:</Text> Use sensors and algorithms
                  </li>
                </ul>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div style={{ padding: "16px", borderRadius: "8px", height: "100%", border: "1px solid #0071e3" }}>
                <Text strong>Netflix</Text>
                <ul style={{ paddingLeft: "20px", marginTop: "8px", marginBottom: 0, fontSize: "14px" }}>
                  <li>
                    <Text strong>Problem:</Text> User doesn't know what to watch
                  </li>
                  <li>
                    <Text strong>Goal:</Text> Suggest enjoyable content
                  </li>
                  <li>
                    <Text strong>Intelligence:</Text> Analyze viewing patterns
                  </li>
                </ul>
              </div>
            </Col>
          </Row>
        </Card>
      </Space>

      <style>{`
        @media (min-width: 769px) {
          .desktop-table {
            display: block;
          }
          .mobile-cards {
            display: none;
          }
        }
        @media (max-width: 768px) {
          .desktop-table {
            display: none;
          }
          .mobile-cards {
            display: block;
          }
        }
      `}</style>
    </div>
  );
};

export default ProblemGoalIntelligence;